/**
 * Static, local page content for the Contact page ("Contacter Ulule"). Like Press/Team, all copy, data
 * and outbound URLs live here so the page stays self-contained and reviewable — it carries no dynamic
 * data. The form is not a first-party endpoint: it hands off to Zendesk (see ZENDESK_NEW_REQUEST_URL).
 */

// ---------- Shared outbound URLs ----------
export const ABOUT_URL = '/about/ulule/'
/** Zendesk help centre home (the "Centre d'aide"). */
export const SUPPORT_HELP_URL = 'https://support.ulule.com/hc/fr-fr'
/**
 * Zendesk hosted "new request" form. Used only for the ticket forms with required custom/conditional
 * fields we don't replicate (see SUBJECT_OPTIONS `handoff`): the user completes them there. Simple
 * forms are submitted directly to the API below instead.
 */
export const ZENDESK_NEW_REQUEST_URL = 'https://support.ulule.com/hc/fr-fr/requests/new'
/** Zendesk end-user API (anonymous submission is enabled + CORS-allowed from our origin). */
export const ZENDESK_REQUESTS_API = 'https://support.ulule.com/api/v2/requests.json'
export const ZENDESK_UPLOADS_API = 'https://support.ulule.com/api/v2/uploads.json'
/** Zendesk article: contacting the project owner directly (referenced by the warn callout + FAQs). */
export const CONTACT_PROJECT_OWNER_URL =
  'https://support.ulule.com/hc/fr-fr/articles/211813109-Comment-contacter-l-équipe-d-un-projet'

// ---------- Hero ----------
export type HeroCta = {
  label: string
  href: string
  /** primary = accent (yellow) fill, secondary = dark (ink) fill. */
  variant: 'primary' | 'secondary'
}

export const HERO_CTAS: HeroCta[] = [
  { label: 'Trouver mon interlocuteur', href: '#objet', variant: 'primary' },
  { label: 'Formulaire de contact', href: '#formulaire', variant: 'secondary' },
]

// ---------- "Quel est l'objet de votre demande ?" — profile accordion ----------
export type ProfileIcon = 'people' | 'megaphone' | 'heart' | 'edit'
export type ProfileVariant = 'blue' | 'yellow' | 'green' | 'red'
type ProfileLink = { label: string; href: string }

export type Profile = {
  icon: ProfileIcon
  variant: ProfileVariant
  title: string
  body: string
  primaryCta: ProfileLink
  /** Optional inline "or…" link beside the primary CTA. */
  secondaryLink?: ProfileLink
  /** Optional "Questions fréquentes" links. */
  faq?: ProfileLink[]
  /** If set, this profile is expanded on load. All profiles are collapsed by default. */
  defaultOpen?: boolean
}

export const PROFILES: Profile[] = [
  {
    icon: 'people',
    variant: 'blue',
    title: 'Je suis un Contributeur / une Contributrice',
    body: 'Vous avez contribué à un projet et avez une question sur votre commande, votre contrepartie ou un remboursement ?',
    primaryCta: {
      label: "Lisez le centre d'aide",
      href: 'https://support.ulule.com/hc/fr-fr/sections/202674909',
    },
    secondaryLink: { label: 'Ou utilisez le formulaire de contact', href: '#formulaire' },
    faq: [
      {
        label: "Je n'ai pas reçu ma contrepartie",
        href: 'https://support.ulule.com/hc/fr-fr/articles/20142079153553-Que-faire-si-je-n-ai-pas-reçu-ma-contrepartie',
      },
      {
        label: 'Demander un remboursement',
        href: 'https://support.ulule.com/hc/fr-fr/articles/213823469-Puis-je-annuler-une-contribution',
      },
      { label: 'Contacter un porteur de projet', href: CONTACT_PROJECT_OWNER_URL },
    ],
  },
  {
    icon: 'megaphone',
    variant: 'yellow',
    title: 'Je suis un Porteur / une Porteuse de projet',
    body: "L'accompagnement se fait directement dans le fil de discussion de votre projet. Posez toutes vos questions à l'équipe directement dans cet espace.",
    primaryCta: { label: 'Connectez-vous', href: '/signin/' },
    secondaryLink: {
      label: "Ou consultez le centre d'aide porteurs",
      href: 'https://support.ulule.com/hc/fr-fr/sections/202674929',
    },
    faq: [
      {
        label: 'Lancer une collecte',
        href: 'https://support.ulule.com/hc/fr-fr/articles/212424225-Proposer-mon-projet',
      },
      { label: 'Gérer ma campagne', href: 'https://support.ulule.com/hc/fr-fr/sections/202674949' },
      {
        label: 'Recevoir mes fonds',
        href: 'https://support.ulule.com/hc/fr-fr/articles/211813529-Comment-recevoir-les-sommes-collectées',
      },
    ],
  },
  {
    icon: 'heart',
    variant: 'green',
    title: 'Je souhaite en savoir plus sur vos offres de partenariats',
    body: 'Ulule connecte les marques à une nouvelle génération de créateurs-influenceurs engagés, pour des campagnes participatives à fort impact social et narratif. Contactez-nous sur la page ci-dessous pour en savoir plus.',
    primaryCta: { label: 'Découvrir nos dispositifs', href: 'https://partenaires.ulule.com' },
  },
  {
    icon: 'edit',
    variant: 'red',
    title: 'Je suis Journaliste ou Blogueur',
    body: "Vous souhaitez parler d'Ulule dans vos médias ? Bonne idée 😎 Contact, infos et visuels à télécharger sur l'Espace Presse.",
    primaryCta: { label: 'Espace presse', href: '/about/press/' },
  },
]

// ---------- Contact form section (#formulaire) ----------
export const FORM_ETA = 'Réponse sous 24 h ouvrées'

/**
 * Subject dropdown. Each option maps to a Zendesk ticket form (`ticketFormId`, read from the live
 * `requests/new` form selector). Simple forms are submitted directly to the API; `handoff` forms have
 * required custom/conditional fields we don't collect, so they open Zendesk's hosted form instead.
 */
export type SubjectOption = { label: string; ticketFormId: string; handoff?: boolean }
export const SUBJECT_PLACEHOLDER = '— Choisir un sujet —'
export const SUBJECT_OPTIONS: SubjectOption[] = [
  {
    label: "Lancement d'une collecte en financement participatif",
    ticketFormId: '14674741450769',
    handoff: true,
  },
  {
    label: "J'ai une question sur ma contribution ou je n'ai pas reçu ma contrepartie",
    ticketFormId: '14675634123793',
    handoff: true,
  },
  { label: 'Formations proposées par Ulule', ticketFormId: '14675767856145' },
  { label: "Devenir partenaire d'Ulule", ticketFormId: '14675790936337' },
  { label: 'Incident technique', ticketFormId: '14675814767377' },
]

/** "Vos questions les plus fréquentes" links in the help callout. */
export const HELP_LINKS: ProfileLink[] = [
  {
    label: "Je n'ai pas reçu ma contrepartie",
    href: 'https://support.ulule.com/hc/fr-fr/articles/20142079153553-Que-faire-si-je-n-ai-pas-reçu-ma-contrepartie',
  },
  {
    label: 'Demander un remboursement',
    href: 'https://support.ulule.com/hc/fr-fr/articles/213823469-Puis-je-annuler-une-contribution',
  },
  {
    label: 'Changer ma contrepartie',
    href: 'https://support.ulule.com/hc/fr-fr/articles/211813169-Puis-je-changer-ma-contrepartie',
  },
  {
    label: 'Si le projet ne voit pas le jour',
    href: 'https://support.ulule.com/hc/fr-fr/articles/211813009-Que-se-passe-t-il-si-le-projet-que-j-ai-soutenu-ne-voit-pas-le-jour',
  },
  {
    label: 'Moyens de paiement acceptés',
    href: 'https://support.ulule.com/hc/fr-fr/articles/115002097249-Quels-sont-les-moyens-de-paiement-acceptés',
  },
  {
    label: 'Obtenir un reçu fiscal ou une facture',
    href: 'https://support.ulule.com/hc/fr-fr/articles/115002097329-Obtenir-un-reçu-fiscal-ou-une-facture',
  },
  { label: 'Contacter un porteur de projet', href: CONTACT_PROJECT_OWNER_URL },
  {
    label: 'Lancer une collecte sur Ulule',
    href: 'https://support.ulule.com/hc/fr-fr/articles/212424225-Proposer-mon-projet',
  },
  { label: 'Gérer ma campagne', href: 'https://support.ulule.com/hc/fr-fr/sections/202674949' },
  {
    label: 'Recevoir mes fonds',
    href: 'https://support.ulule.com/hc/fr-fr/articles/211813529-Comment-recevoir-les-sommes-collectées',
  },
  { label: 'Signaler un incident technique', href: ZENDESK_NEW_REQUEST_URL },
]

export const PRIVACY_EMAIL = 'privacy@ulule.com'
export const PRIVACY_POLICY_URL = '/about/privacy/'
